import { z } from 'zod'
import { DeliveryMode, FieldType, ParticipantKind, RoutingMode, SenderOrder } from './enums.js'
import { FieldFormatting } from './field-attributes.js'
import { EnvelopeParticipantSpec } from './api.js'
import { HASH_STRING_PATTERN, SHA256_HEX_PATTERN } from './version.js'

/**
 * The MCP approval spine — SM3 (B7.3, B9, §14.1; MCP-AC-036..043, 109/110, DEC-06).
 *
 * The shape of this contract is the whole safety argument, so it is worth stating once:
 *
 * 1. **A model prepares; a person approves.** `McpPrepareRequest` freezes an action and returns a
 *    `review_id` and a `review_url` — and **no token**. There is no field anywhere in this file by
 *    which a caller can assert an approval, and the prepare result's `review_token` is the literal
 *    `null`, so "prepare also handed me the token" is unrepresentable rather than merely refused.
 * 2. **The approval token is issued after recorded human approval**, over the grant's own
 *    authenticated channel (`signbento_get_review_status`), is single-use, and expires in five
 *    minutes. A host-rendered confirmation (MRTR) never produces one: `McpMrtrRequestState` is
 *    declared here as a *clarification* handle with `consequential: false`, and the commit contract
 *    requires an approval, not a clarification.
 * 3. **Nothing in a prepare or a commit is content.** The source is named by a workspace item and a
 *    revision, never by bytes, a path, a base64 blob or document text — the schemas are `strict()`,
 *    so an attempt to send any of those is a schema violation instead of a silently ignored field.
 *    The one value a model may write is a sender pre-fill on a placed field (`P17-10`, decided by
 *    Hamza on 2026-10-28): it is shown on the review page, bound into the fields digest, and
 *    refused for signatures, initials, the signing date, identity numbers and secrets.
 * 4. **Digests bind the approval.** An approval records the account, the grant, the action, the
 *    frozen source revision and four content digests; a material change (a new source revision, a
 *    changed participant set, changed fields) produces different digests and the old approval is
 *    invalid, not merely stale.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()

/* ---------------------------------- states --------------------------------- */

/**
 * The review's own lifecycle. `AWAITING_APPROVAL` is the only non-terminal state a caller can act
 * on; `APPROVED` is the window in which the token exists; `INVALIDATED` is a recorded material
 * change (D-22), which is deliberately distinct from `EXPIRED` so a person can be told which
 * happened.
 */
export const MCP_REVIEW_STATES = [
  'AWAITING_APPROVAL',
  'APPROVED',
  'REJECTED',
  'COMMITTED',
  'EXPIRED',
  'INVALIDATED',
] as const
export const McpReviewState = z.enum(MCP_REVIEW_STATES)
export type McpReviewState = z.infer<typeof McpReviewState>

/** Why an approval stopped being usable. Machine codes only — never prose, never content. */
export const MCP_APPROVAL_INVALIDATION_REASONS = [
  'SOURCE_REVISION_CHANGED',
  'PARTICIPANTS_CHANGED',
  'FIELDS_CHANGED',
  'ACTION_CHANGED',
  'GRANT_REVOKED',
  'GRANT_REVISION_CHANGED',
  'ACCOUNT_INACTIVE',
  'REVIEW_SUPERSEDED',
] as const
export const McpApprovalInvalidationReason = z.enum(MCP_APPROVAL_INVALIDATION_REASONS)
export type McpApprovalInvalidationReason = z.infer<typeof McpApprovalInvalidationReason>

/**
 * The approval's lifetime (B9): five minutes from the recorded approval. It is a product constant
 * rather than a register value because it is a review-window decision, not a calibrated budget.
 */
export const APPROVAL_TTL_SECONDS = 300

/** How long a prepared review stays answerable before it expires unapproved. */
export const REVIEW_TTL_SECONDS = 15 * 60

/** The MRTR clarification handle's lifetime: short by construction (2026-07-28 MRTR). */
export const MRTR_REQUEST_STATE_TTL_SECONDS = 120

/**
 * How many clarifications one request digest may raise. A second is allowed (a person may answer
 * the wrong thing once); a third is refused, so a clarification loop cannot be used to keep a model
 * in a dialogue — the bound is on the *request*, not on the tool.
 */
export const MRTR_MAX_CLARIFICATIONS_PER_REQUEST = 2

/* ------------------------------ frozen content ------------------------------ */

/**
 * A field **assignment**: which field, whose it is, whether it is required. No type and no value —
 * `P17-10` is open, and this shape is what keeps that decision open rather than pre-empting it.
 */
export const McpFieldAssignment = z
  .object({
    field_id: z.string().min(1).max(64),
    /** The role the field belongs to; it must name a participant role in the frozen set. */
    role_id: z.string().min(1).max(64).optional(),
    required: z.boolean(),
    locked: z.boolean().optional(),
  })
  .strict()
export type McpFieldAssignment = z.infer<typeof McpFieldAssignment>

/** The field types an agent may place: every local `FieldType`, with the same meaning. */
export const MCP_PLACEMENT_TYPES = FieldType.options
export const McpPlacementType = FieldType
export type McpPlacementType = FieldType

/**
 * Which D7 settings mean something for each type, as the editor's inspector offers them. A setting
 * given for a type it means nothing for is refused, not dropped, so the review never shows a
 * setting that will not apply.
 */
export const MCP_PLACEMENT_SETTINGS = {
  /** A value the sender fills in (P17-10): typed text fields only. */
  prefill: ['FULL_NAME', 'EMAIL', 'TEXT', 'COMPANY', 'TITLE'],
  locked: ['FULL_NAME', 'EMAIL', 'TEXT', 'COMPANY', 'TITLE', 'CHECKBOX'],
  max_length: ['TEXT'],
  validation: ['FULL_NAME', 'EMAIL', 'TEXT', 'COMPANY', 'TITLE'],
  formatting: ['FULL_NAME', 'EMAIL', 'TEXT', 'COMPANY', 'TITLE', 'FORMULA'],
  expression: ['FORMULA'],
} as const satisfies Readonly<Record<string, readonly FieldType[]>>

export type McpPlacementSetting = keyof typeof MCP_PLACEMENT_SETTINGS

/** True when a D7 setting means something for a field type. */
export function placementSettingApplies(setting: McpPlacementSetting, type: FieldType): boolean {
  return (MCP_PLACEMENT_SETTINGS[setting] as readonly FieldType[]).includes(type)
}

/**
 * The validation presets an agent may set. `SSN` is left out because an identity number is not
 * something an assistant should set up a document to collect for it, and `Custom` because the
 * register's pattern bound (`custom_pattern_max_length`) is not frozen yet. Both stay available in
 * the person's own editor.
 */
export const MCP_VALIDATION_PRESETS = [
  'None',
  'Email',
  'Numbers',
  'Letters',
  'Date',
  'ZIP',
  'ZIP+4',
] as const
export const McpValidationPreset = z.enum(MCP_VALIDATION_PRESETS)
export type McpValidationPreset = z.infer<typeof McpValidationPreset>

/** A placement's own name, so a formula can refer to it: `{subtotal}`. */
export const MCP_PLACEMENT_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,31}$/

const Fraction = z.number().min(0).max(1)

/**
 * How an anchor matches the document's text layer when the placement does not say. Case is
 * ignored, and a match must be a whole word — so an agent's own tag (`/sn1/`) is not found inside
 * a longer token, and a common word does not match inside another word. The browser applies these
 * rules where the document is rendered; the service still never reads document text.
 */
export const MCP_ANCHOR_CASE_SENSITIVE_DEFAULT = false
export const MCP_ANCHOR_WHOLE_WORD_DEFAULT = true

/** A box on one page: 1-based page, origin top-left, every value a fraction of the page. */
export const McpPlacementBox = z
  .object({
    kind: z.literal('BOX'),
    page: z.number().int().positive(),
    x: Fraction,
    y: Fraction,
    w: Fraction.refine((value) => value > 0),
    h: Fraction.refine((value) => value > 0),
  })
  .strict()
  .refine((box) => box.x + box.w <= 1 && box.y + box.h <= 1, { message: 'box leaves the page' })
export type McpPlacementBox = z.infer<typeof McpPlacementBox>

/**
 * A position relative to text in the PDF's own text layer, such as "Signature:". The service never
 * reads document text, so an anchor resolves where the document is rendered: the person's own
 * browser, on the review page. The rules are explicit rather than guessed: which occurrences, the
 * offset from the text's top-left and its units, and what a missing anchor means.
 */
export const McpPlacementAnchor = z
  .object({
    kind: z.literal('ANCHOR'),
    text: z.string().trim().min(1).max(200),
    occurrence: z.enum(['FIRST', 'ALL']),
    offset: z
      .object({
        x: z.number().min(-2000).max(2000),
        y: z.number().min(-2000).max(2000),
        units: z.enum(['POINTS', 'PAGE_FRACTION']),
      })
      .strict(),
    /** The field's size as fractions of the page; the type's usual size when absent. */
    size: z
      .object({
        w: Fraction.refine((value) => value > 0),
        h: Fraction.refine((value) => value > 0),
      })
      .strict()
      .optional(),
    /** `FAIL` blocks approval until the placement is fixed; `SKIP` drops it and says so. */
    if_missing: z.enum(['FAIL', 'SKIP']),
    /** Match case exactly; default `false` (`MCP_ANCHOR_CASE_SENSITIVE_DEFAULT`). */
    case_sensitive: z.boolean().optional(),
    /** Match whole words only; default `true` (`MCP_ANCHOR_WHOLE_WORD_DEFAULT`). */
    whole_word: z.boolean().optional(),
  })
  .strict()
export type McpPlacementAnchor = z.infer<typeof McpPlacementAnchor>

/**
 * A field an agent places. `position` is a union so new kinds can be added without a break. The
 * settings are the D7 attributes the editor has; the text, tooltip and length bounds are register
 * values that prepare applies (`text_field_max_scalars`, `tooltip_max_scalars`), so the large
 * ceilings here only stop an absurd payload.
 */
export const McpFieldPlacement = z
  .object({
    type: McpPlacementType,
    /** A signer role in the frozen participant set. */
    role_id: z.string().min(1).max(64),
    required: z.boolean(),
    position: z.union([McpPlacementBox, McpPlacementAnchor]),
    /** The placement's name, for a formula to refer to. Unique within the request. */
    key: z.string().regex(MCP_PLACEMENT_KEY_PATTERN).optional(),
    label: z.string().trim().min(1).max(200).optional(),
    tooltip: z.string().trim().min(1).max(2000).optional(),
    /** Holds the sender's `prefill` fixed. Without a value, the signer still fills the field. */
    locked: z.boolean().optional(),
    max_length: z.number().int().positive().max(100_000).optional(),
    validation: McpValidationPreset.optional(),
    formatting: FieldFormatting.optional(),
    /**
     * The sender's pre-filled value (P17-10). The signer can change it unless the field is locked.
     * Never a signature, initials or the signing date, an identity number or a secret.
     */
    prefill: z.string().min(1).max(100_000).optional(),
    /** A FORMULA field's expression, naming other placements of the same signer by `{key}`. */
    expression: z.string().min(1).max(500).optional(),
  })
  .strict()
export type McpFieldPlacement = z.infer<typeof McpFieldPlacement>

/**
 * What prepare did with each placement, in request order, then the defaults it added. `DEFAULTED`
 * entries are the signature and date per signer that prepare adds when no placement was given.
 * `ANCHOR_PENDING` means the anchor resolves when the person opens the review page.
 */
export const McpPlacementReport = z
  .object({
    placements: z.array(
      z
        .object({
          index: z.number().int().nonnegative(),
          type: McpPlacementType,
          role_id: z.string().min(1).max(64),
          status: z.enum(['PLACED', 'DEFAULTED', 'ANCHOR_PENDING']),
          /**
           * The anchor matching that will run in the browser, with the defaults applied. Present
           * only for an ANCHOR placement; a BOX reports its page in the placement itself.
           */
          case_sensitive: z.boolean().optional(),
          whole_word: z.boolean().optional(),
        })
        .strict(),
    ),
    defaults_added: z.boolean(),
  })
  .strict()
export type McpPlacementReport = z.infer<typeof McpPlacementReport>

/**
 * The frozen source: a workspace item **at a revision**. The bytes are never part of this contract —
 * the item is the person's own published copy and the service already holds it, so nothing here
 * can carry document text (MCP-AC-041).
 */
export const McpPrepareSource = z
  .object({
    kind: z.literal('WORKSPACE_ITEM'),
    item_id: Id,
    /** The workspace item version the caller read. A newer version invalidates the approval. */
    revision: z.number().int().positive(),
  })
  .strict()
export type McpPrepareSource = z.infer<typeof McpPrepareSource>

/** The intended action, as the C5 handoff contract also names it. */
export const McpActionRef = z
  .object({
    capability_id: z.string().min(1).max(128),
    action_ref: z.string().min(1).max(255),
  })
  .strict()
export type McpActionRef = z.infer<typeof McpActionRef>

/**
 * The prepare request — what a model may propose. It is `strict()` and has no field for a document,
 * a path, a base64 blob, a field value or an approval: the four ways this could go wrong are
 * unrepresentable rather than validated against a blocklist.
 */
export const McpPrepareRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    capability_id: z.string().min(1).max(128),
    action_ref: z.string().min(1).max(255),
    source: McpPrepareSource,
    title: z.string().min(1).max(300),
    delivery_mode: DeliveryMode,
    routing_mode: RoutingMode,
    sender_order: SenderOrder,
    expires_in_days: z.number().int().positive().max(90).optional(),
    participants: z.array(EnvelopeParticipantSpec).min(1).max(20),
    fields: z.array(McpFieldAssignment).max(500).default([]),
    /**
     * Where the signers' fields go. Absent or empty: one signature and one date signed per signer,
     * at the end of the last page. Given: every signer needs a SIGNATURE among them.
     */
    placements: z.array(McpFieldPlacement).max(200).optional(),
    /**
     * B11.2, and deliberately **required**: the person's own statement of whether a completed copy
     * should be saved back into the workspace. It is never inferred from the grant's scopes — a
     * scope is an operation, and "keep a copy of my completed agreement" is not one.
     *
     * Saving the copy is not built yet, so prepare refuses `true`
     * (`error.mcp.save_completed_copy.unavailable`) rather than freeze a choice it would ignore.
     */
    save_completed_copy: z.boolean(),
    /** MRTR clarification: the handle from a previous `input_required` answer, when there was one. */
    request_state: z.string().min(1).max(4096).optional(),
    /** The answer to that clarification. */
    clarification_choice: z.string().min(1).max(128).optional(),
  })
  .strict()
export type McpPrepareRequest = z.infer<typeof McpPrepareRequest>

/* ---------------------------------- review --------------------------------- */

/**
 * The credit effect of the frozen action — one shape for a prepared draft and for an operational
 * edit, so the trusted surface renders one credit sentence whichever review it is looking at.
 * `applied_at: 'ACTIVATION'` says when it lands: a review that is never committed costs nothing,
 * and a failure before activation consumes nothing (MCP-AC-047).
 */
export const McpCreditEffect = z
  .object({
    kind: z.enum(['none', 'trial', 'credit']),
    debited: z.number().int().nonnegative(),
    available_after: z.number().int().nonnegative(),
    /** When the effect lands: this slice never debits before the action is performed. */
    applied_at: z.literal('ACTIVATION'),
  })
  .strict()
export type McpCreditEffect = z.infer<typeof McpCreditEffect>

/* --------------------------- operational reviews ---------------------------- */

/**
 * The operational families the MCP surface serves as prepare/commit pairs — SM5 (B7.5, B7.3).
 *
 * `RESEND` is the reminder family as well: the application has one path for both (a reminder *is*
 * a second invitation for the same slot), so the surface has one family with one name.
 */
export const MCP_OPERATION_KINDS = ['CORRECTION', 'RESEND', 'VOID', 'EXPIRY_EXTENSION'] as const
export const McpOperationKind = z.enum(MCP_OPERATION_KINDS)
export type McpOperationKind = z.infer<typeof McpOperationKind>

/**
 * What a person is being asked to approve when the frozen action is an operational edit rather
 * than a draft. It is carried inside the review summary, so one surface, one approval and one
 * status poll serve both kinds of review — and the action's own description is in the payload the
 * person's surface receives rather than in a side channel.
 *
 * `changes` names the fields a correction edits and their new values: the person must see what the
 * identity is *becoming*, and the host that proposed it already knows. Nothing here is document
 * content, and no field carries a value the operation did not already receive.
 */
export const McpReviewOperation = z
  .object({
    kind: McpOperationKind,
    envelope_id: Id,
    /** The participant the operation is about; `null` for the two envelope-level operations. */
    recipient_id: Id.nullable(),
    /** The participant's display name, so a surface can say *whose* step this is. */
    recipient_name: z.string().min(1).max(200).nullable(),
    /**
     * The revision the prepare read: the participant's capability version for a recipient-targeted
     * operation, the envelope's operational revision for a void or an extension. A commit re-reads
     * it and answers `REVISION_CONFLICT` when it moved (STM-10).
     */
    expected_revision: z.number().int().nonnegative(),
    /** The correction's edited fields and their new values. Empty for every other family. */
    changes: z
      .array(
        z
          .object({
            field: z.enum(['name', 'email', 'role_id', 'locale_code']),
            to: z.string().min(1).max(320),
          })
          .strict(),
      )
      .max(4),
    /** The correction's own flag: does it also hand over a new invitation? */
    resend: z.boolean(),
    /** The extension's requested window in days from the original activation; `null` otherwise. */
    requested_days: z.number().int().positive().nullable(),
    /** The void's own reason, exactly as the person approved it; `null` for every other family. */
    reason: z.string().max(500).nullable(),
    /** The agreement's delivery mode, so the surface can say which branch this is. */
    delivery_mode: DeliveryMode,
    /** The managed branch: the invitation is queued through SignBento's own provider. */
    managed: z.boolean(),
    /** Stated because it is a requirement, not an implication: this branch needs `delivery.write`. */
    requires_delivery_scope: z.boolean(),
    /** What the action does to the agreement, as a machine code the surface can localize. */
    effect: z.enum([
      'REPLACE_CAPABILITY_AND_EDIT_IDENTITY',
      'QUEUE_ANOTHER_INVITATION',
      'REVOKE_EVERY_CAPABILITY_AND_CLOSE',
      'LENGTHEN_DEADLINE',
    ]),
  })
  .strict()
export type McpReviewOperation = z.infer<typeof McpReviewOperation>

/**
 * The canonical review summary — the whole of what the trusted surface renders and what the model
 * is told was frozen. It is **completeness** (MCP-AC-038): document identity, the exact participant
 * set and order, the field assignments, the deadline, the delivery mode and the credit effect. A
 * surface that rendered anything else would be showing a different action than the one approved.
 *
 * `document.preview` is deliberately `null`: the surface renders the preview from the workspace
 * item it already has under the person's own session, so no content crosses the MCP boundary.
 *
 * Two fields make the same summary serve an operational review (SM5) without a second shape:
 * `document.source_kind` names what `document.item_id` is, and `operation` describes the edit when
 * the frozen action is one. A draft review's `operation` is the literal `null`.
 */
export const McpReviewSummary = z
  .object({
    review_id: Id,
    action: McpActionRef,
    /**
     * The agreement's own title, as the assistant set it and the person approves it. It can differ
     * from `document.title`, the name of the workspace copy it is made from.
     */
    title: z.string().min(1).max(300),
    document: z
      .object({
        /**
         * `WORKSPACE_ITEM` for a prepared draft (SM3) and `AGREEMENT` for an operational review
         * (SM5), where `item_id` is the agreement the edit acts on and `revision` is the revision
         * the prepare read.
         */
        source_kind: z.enum(['WORKSPACE_ITEM', 'AGREEMENT']),
        item_id: Id,
        revision: z.number().int().positive(),
        title: z.string().min(1).max(300),
        /** Identity without content: the digest the service itself computed over the bytes. */
        content_digest: z.string().regex(HASH_STRING_PATTERN),
        bytes: z.number().int().nonnegative(),
        pages: z.number().int().nonnegative(),
        /** Always `null` over MCP: the trusted surface renders the preview, not this payload. */
        preview: z.null(),
      })
      .strict(),
    participants: z.array(
      z
        .object({
          role_id: z.string().min(1).max(64).nullable(),
          kind: ParticipantKind,
          name: z.string().min(1).max(200),
          email: z.string().min(1).max(320).nullable(),
          order_index: z.number().int().nonnegative(),
          is_sender: z.boolean(),
        })
        .strict(),
    ),
    fields: z.array(McpFieldAssignment),
    /** The frozen placements, defaults included, in the order the report lists them. */
    placements: z.array(McpFieldPlacement).max(240),
    placement_report: McpPlacementReport,
    deadline: z
      .object({
        expires_in_days: z.number().int().positive(),
        /** The plan's ceiling for this account, so the surface can explain the choice. */
        max_expires_in_days: z.number().int().positive(),
      })
      .strict(),
    delivery_mode: DeliveryMode,
    routing_mode: RoutingMode,
    sender_order: SenderOrder,
    credit_effect: McpCreditEffect,
    save_completed_copy: z.boolean(),
    /**
     * The operational edit this review approves, or the literal `null` when the frozen action is a
     * prepared draft. Present so one surface can render both kinds of review honestly.
     */
    operation: McpReviewOperation.nullable(),
  })
  .strict()
export type McpReviewSummary = z.infer<typeof McpReviewSummary>

/**
 * The approval's binding (B9). Every part of it is a server-derived value: the account and grant
 * come from the authenticated call, the revision from the frozen source, and the digests from the
 * frozen content. Nothing here is a caller assertion.
 */
export const McpApprovalBinding = z
  .object({
    approval_id: Id,
    review_id: Id,
    account_user_id: Id,
    grant_id: Id,
    authorization_revision: z.number().int().nonnegative(),
    action: McpActionRef,
    source_revision: z.number().int().positive(),
    digests: z
      .object({
        source: z.string().regex(SHA256_HEX_PATTERN),
        participants: z.string().regex(SHA256_HEX_PATTERN),
        fields: z.string().regex(SHA256_HEX_PATTERN),
        action: z.string().regex(SHA256_HEX_PATTERN),
      })
      .strict(),
    approved_at: IsoDateTime,
    expires_at: IsoDateTime,
    consumed_at: IsoDateTime.nullable(),
    invalidated_at: IsoDateTime.nullable(),
    invalidation_reason: McpApprovalInvalidationReason.nullable(),
  })
  .strict()
export type McpApprovalBinding = z.infer<typeof McpApprovalBinding>

/**
 * The prepare result. `review_token` is the literal `null` — the token does not exist yet, and a
 * model that wants one has to wait for a person to record an approval (MCP-AC-036).
 */
export const McpPrepareResult = z
  .object({
    review_id: Id,
    review_url: z.string().min(1).max(2048),
    state: McpReviewState,
    expires_at: IsoDateTime,
    summary: McpReviewSummary,
    /** Always `null` here: the approval token is issued after recorded human approval. */
    review_token: z.null(),
    /** Stated in the payload so no caller can claim it was implied. */
    approval_recorded: z.literal(false),
  })
  .strict()
export type McpPrepareResult = z.infer<typeof McpPrepareResult>

/**
 * A bounded, non-consequential clarification (2026-07-28 MRTR). Every field is a machine value:
 * `question_key` and `label_key` are message keys, never document text, so a document cannot speak
 * through a question. `consequential` is the literal `false` — the clarification never stands in
 * for an approval.
 */
export const McpClarification = z
  .object({
    clarification_id: Id,
    question_key: z.string().min(1).max(160),
    choices: z
      .array(
        z
          .object({
            choice_id: z.string().min(1).max(128),
            label_key: z.string().min(1).max(160),
          })
          .strict(),
      )
      .min(2)
      .max(10),
    consequential: z.literal(false),
    /** Stated so a host cannot present a clarification as an approval prompt. */
    is_approval: z.literal(false),
  })
  .strict()
export type McpClarification = z.infer<typeof McpClarification>

/** What an `input_required` tool answer carries: the clarification and its integrity-protected handle. */
export const McpInputRequired = z
  .object({
    clarification: McpClarification,
    /**
     * AEAD/HMAC integrity-protected, principal-bound, short-TTL, request-digest-bound and
     * replay-bounded. It is treated as attacker-controlled input on the way back in, never as a
     * bearer credential for anything.
     */
    request_state: z.string().min(1).max(4096),
    expires_at: IsoDateTime,
  })
  .strict()
export type McpInputRequired = z.infer<typeof McpInputRequired>

/** The status a poller sees. The token appears only in the approved window, and only once. */
export const McpReviewStatusResult = z
  .object({
    review_id: Id,
    state: McpReviewState,
    expires_at: IsoDateTime,
    summary: McpReviewSummary,
    approval: McpApprovalBinding.nullable(),
    /** Present only while `state === 'APPROVED'`: issued after recorded human approval. */
    review_token: z.string().min(1).max(512).nullable(),
    invalidation_reason: McpApprovalInvalidationReason.nullable(),
    /** True once the approval was consumed by a commit, so a replay is visible as one. */
    committed: z.boolean(),
  })
  .strict()
export type McpReviewStatusResult = z.infer<typeof McpReviewStatusResult>

/* ---------------------------------- commit --------------------------------- */

/**
 * The commit request. `review_id` + `review_token` are the *only* inputs: there is no field for a
 * revision, a participant set or a source, so "commit something other than what was approved" is
 * unrepresentable (MCP-AC-039).
 */
export const McpCommitRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    review_id: Id,
    review_token: z.string().min(1).max(512).optional(),
  })
  .strict()
export type McpCommitRequest = z.infer<typeof McpCommitRequest>

/**
 * The commit result — the stored result a replay returns verbatim. `capability_links` is always
 * `null` (a signing link is SM4's explicit reveal, never a side effect of a commit), and the
 * continuation carries the frozen draft so the person's own browser performs the write
 * (`write_execution: secure_browser_continuation`).
 */
export const McpCommitResult = z
  .object({
    review_id: Id,
    state: z.literal('COMMITTED'),
    committed_at: IsoDateTime,
    action: McpActionRef,
    /** The frozen draft the browser completes. `draft_id` is the business key: one envelope. */
    draft: z
      .object({
        draft_id: Id,
        revision: z.number().int().nonnegative(),
        title: z.string().min(1).max(300),
        delivery_mode: DeliveryMode,
        routing_mode: RoutingMode,
        sender_order: SenderOrder,
        expires_in_days: z.number().int().positive(),
        participants: z.array(EnvelopeParticipantSpec),
        fields: z.array(McpFieldAssignment),
        placements: z.array(McpFieldPlacement).max(240),
        workspace: z
          .object({
            item_id: Id,
            revision: z.number().int().positive(),
            save_completed_copy: z.boolean(),
          })
          .strict()
          .nullable(),
      })
      .strict(),
    continuation: z
      .object({
        kind: z.literal('BROWSER_COMPLETION'),
        url: z.string().min(1).max(2048),
        expires_at: IsoDateTime,
        /** The write happens in the browser: the host never holds send or sign authority. */
        authority: z.literal('SEAL_UPLOAD_AND_ACTIVATE'),
        host_holds_send_authority: z.literal(false),
      })
      .strict(),
    credit_effect: McpCreditEffect,
    /** Never a signing link: revealing one is SM4's explicit, separate operation. */
    capability_links: z.null(),
    /** True when this answer came from the stored result rather than a new consumption. */
    idempotent_replay: z.boolean(),
  })
  .strict()
export type McpCommitResult = z.infer<typeof McpCommitResult>

/**
 * The short-lived browser edit continuation. It carries **no** send and **no** sign authority —
 * both are literals, so a surface cannot describe it as more than it is.
 */
export const McpEditorLinkResult = z
  .object({
    continuation_id: Id,
    url: z.string().min(1).max(2048),
    expires_at: IsoDateTime,
    scope: z.enum(['REVIEW_DRAFT', 'ENVELOPE_DRAFT']),
    target_id: Id,
    can_send: z.literal(false),
    can_sign: z.literal(false),
    /** What the continuation *is* allowed to do, as a machine value. */
    authority: z.literal('EDIT_FIELDS_AND_METADATA'),
  })
  .strict()
export type McpEditorLinkResult = z.infer<typeof McpEditorLinkResult>

/* ----------------------- trusted-surface request shapes --------------------- */

/** What the review surface records. `approve: true` is the person's recorded decision. */
export const McpReviewDecisionRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    approve: z.boolean(),
    /** A machine reason for a rejection; never prose and never content. */
    reason_key: z.string().min(1).max(160).optional(),
  })
  .strict()
export type McpReviewDecisionRequest = z.infer<typeof McpReviewDecisionRequest>

/**
 * The owner's client links a committed draft review to the agreement it sent from the browser
 * (`POST /v1/mcp/reviews/:review_id/agreement`), so the review's agreement can be found again.
 */
export const McpReviewAgreementLinkRequest = z.object({ envelope_id: Id }).strict()
export type McpReviewAgreementLinkRequest = z.infer<typeof McpReviewAgreementLinkRequest>

export const McpReviewAgreementLinkResponse = z.object({ review_id: Id, envelope_id: Id }).strict()
export type McpReviewAgreementLinkResponse = z.infer<typeof McpReviewAgreementLinkResponse>

export const McpReviewDecisionResponse = z
  .object({
    review_id: Id,
    state: McpReviewState,
    approval: McpApprovalBinding.nullable(),
    /** The token is delivered to the host through its own authenticated status poll, not here. */
    token_issued_to_host: z.boolean(),
  })
  .strict()
export type McpReviewDecisionResponse = z.infer<typeof McpReviewDecisionResponse>

/**
 * The continuation the person's own browser redeems. Two kinds exist and the answer states which
 * one it is: `EDIT_FIELDS_AND_METADATA` (the short-lived editor link, no send and no sign) and
 * `SEAL_UPLOAD_AND_ACTIVATE` (the completion continuation, which may finish the action). Neither
 * ever gives the *host* send authority.
 */
export const McpContinuationAuthority = z.enum([
  'EDIT_FIELDS_AND_METADATA',
  'SEAL_UPLOAD_AND_ACTIVATE',
])
export type McpContinuationAuthority = z.infer<typeof McpContinuationAuthority>

export const McpContinuationResponse = z
  .object({
    review_id: Id,
    state: McpReviewState,
    draft: McpCommitResult.shape.draft,
    /** The browser, not the host, may seal, upload and activate — or edit, and only edit. */
    authority: McpContinuationAuthority,
    /** Whether this continuation may complete the action at all. */
    can_send: z.boolean(),
    can_sign: z.boolean(),
    host_holds_send_authority: z.literal(false),
    expires_at: IsoDateTime,
  })
  .strict()
export type McpContinuationResponse = z.infer<typeof McpContinuationResponse>

/* --------------------------- injection boundary ---------------------------- */

/**
 * Field names that would turn a tool argument into a content or instruction channel (MCP-AC-041/042).
 * The schemas are already `strict()` and name no such field; this list exists so the boundary is
 * asserted in a test over the *live* request objects as well as in the schema, and so a future
 * optional field cannot be added without the test failing.
 */
export const FORBIDDEN_MCP_CONTENT_FIELDS: readonly string[] = [
  'document_text',
  'document_bytes',
  'document_content',
  'content_base64',
  'base64',
  'plaintext',
  'file_path',
  'local_path',
  'instructions',
  'system_prompt',
  'recipient_override',
  'action_override',
]

/**
 * The forbidden field names present anywhere in a value, sorted. It walks objects and arrays (not
 * strings: a *value* is data, and rejecting values containing the word "instructions" would refuse
 * a legitimate title).
 */
export function forbiddenContentFieldsIn(value: unknown): readonly string[] {
  const found = new Set<string>()
  const stack: unknown[] = [value]
  while (stack.length > 0) {
    const current = stack.pop()
    if (Array.isArray(current)) {
      for (const item of current) stack.push(item)
      continue
    }
    if (typeof current !== 'object' || current === null) continue
    for (const [key, nested] of Object.entries(current)) {
      if (FORBIDDEN_MCP_CONTENT_FIELDS.includes(key)) found.add(key)
      stack.push(nested)
    }
  }
  return [...found].sort()
}
